import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Tag, Plus, Download, Copy, Search, X, Loader2, ArrowLeft, ArrowRight,
  CheckCircle2, XCircle, AlertTriangle, RefreshCw, FileText as FileIcon, Trash2,
  Pencil,
} from "lucide-react";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import { Checkbox } from "../../ui/checkbox";

import { adminApi, useAuth, formatApiError } from "../../../context/AuthContext";
import CustomStatusSection from "./CustomStatusSection";
import LabelEditDialog from "./LabelEditDialog";
import ShipmentLookupDialog from "./ShipmentLookupDialog";
import { ShipmentPrealertLink } from "../operational/ShipmentPrealertLink";
import { CentralAgentReceipt } from "../operational/CentralAgentReceipt";
import { ShipmentScanResult } from "../operational/ShipmentScanResult";

const EMPTY_PARTY = {
  name: "", company: "", phone: "", email: "",
  street: "", house_number: "", postal_code: "", city: "", country: "",
};
const EMPTY_SHIPMENT = {
  piece_count: "", weight_kg: "",
  length_cm: "", width_cm: "", height_cm: "",
  contents_description: "", value: "", currency: "EUR",
  destination: "", internal_note: "",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

const STATUS_BADGE = {
  concept:     { label: "Concept",      cls: "bg-gray-100 text-gray-700 border-gray-300" },
  aangemaakt:  { label: "Aangemaakt",   cls: "bg-amber-100 text-amber-800 border-amber-300" },
  gedownload:  { label: "Gedownload",   cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  geannuleerd: { label: "Geannuleerd",  cls: "bg-red-100 text-red-700 border-red-300" },
};

export default function ShippingLabelsSection({ prefilter = null, onClearPrefilter, initialShipmentId = null } = {}) {
  const [tab, setTab] = useState("overview");
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.get("/admin/shipping-labels/meta");
        setMeta(r.data);
      } catch (err) {
        toast.error(formatApiError(err) || "Meta laden mislukt");
      }
    })();
  }, []);

  return (
    <div className="space-y-4" data-testid="shipping-labels-section">
      <div className="flex items-center justify-between">
        <h1 className="font-chivo font-black text-2xl text-[#0A192F] flex items-center gap-2">
          <Tag className="w-6 h-6" /> Tracking &amp; Labels
        </h1>
        {meta?.next_tracking_number_hint && (
          <span className="text-xs text-gray-600">
            Volgend trackingnummer: <span className="font-mono font-bold text-[#0A192F]">{meta.next_tracking_number_hint}</span>
          </span>
        )}
      </div>

      {prefilter && prefilter.type === "customer" && (
        <div
          className="flex items-center justify-between gap-3 bg-blue-50 border border-blue-200 px-3 py-2 rounded-xl"
          data-testid="labels-prefilter-banner"
        >
          <div className="text-xs text-blue-900">
            <span className="font-bold uppercase tracking-wide mr-2">Gefilterd op klant:</span>
            <span className="font-medium" data-testid="labels-prefilter-name">{prefilter.customer_name}</span>
            {prefilter.customer_email && <span className="text-blue-700"> · {prefilter.customer_email}</span>}
          </div>
          <button
            type="button"
            onClick={() => onClearPrefilter?.()}
            className="text-xs text-blue-900 hover:text-blue-700 underline font-medium"
            data-testid="labels-prefilter-clear"
          >
            Filter wissen
          </button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="labels-tab-overview">Overzicht</TabsTrigger>
          <TabsTrigger value="new" data-testid="labels-tab-new">Nieuw label</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-4">
          <OverviewTab meta={meta} onCreateNew={() => setTab("new")} prefilter={prefilter} initialShipmentId={initialShipmentId} />
        </TabsContent>
        <TabsContent value="new" className="mt-4">
          <LabelWizard meta={meta} onSaved={() => setTab("overview")} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// =================== Overview ===================

function OverviewTab({ meta, onCreateNew, prefilter, initialShipmentId }) {
  const [items, setItems] = useState([]);
  const [flowCounts, setFlowCounts] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [serviceF, setServiceF] = useState("");
  const [payF, setPayF] = useState("");
  const [rangeF, setRangeF] = useState("");
  // P0 SPRINT — canonical lifecycle-filter chip (single select).
  // "all" toont alle actieve zendingen ongeacht status. Andere chips
  // vertalen naar een specifieke query-param combinatie zodat NOOIT een
  // shipment ongewild uit de lijst verdwijnt.
  const [lifecycleF, setLifecycleF] = useState("all");
  const { user, hasPermission } = useAuth();
  const userRole = (user?.role || "").toLowerCase();
  const canLookup = hasPermission ? hasPermission("shipments:lookup") : true;
  const [selected, setSelected] = useState(null);
  const [scanNumber, setScanNumber] = useState("");
  useEffect(() => {
    let active = true;
    if (initialShipmentId) adminApi.get(`/admin/shipping-labels/${encodeURIComponent(initialShipmentId)}`)
      .then(r => { if (active) setSelected(r.data); })
      .catch(e => { if (active) toast.error(formatApiError(e)); });
    return () => { active = false; };
  }, [initialShipmentId]);
  const [reloadTick, setReloadTick] = useState(0);
  const [lookupOpen, setLookupOpen] = useState(false);
  const triggerReload = () => setReloadTick((n) => n + 1);
  const recipientEmailFilter = prefilter?.type === "customer" ? (prefilter.customer_email || "") : "";

  // Meta-driven lifecycle filter definitions — gefilterd op rol.
  const lifecycleFilters = useMemo(() => {
    const list = meta?.lifecycle_filters || [];
    return list.filter((f) => {
      if (!f.requires_role) return true;
      return f.requires_role.includes(userRole);
    });
  }, [meta, userRole]);

  // P0 hotfix — deeplink-ondersteuning. Wanneer de URL `?label={id}` of
  // `?shipment_id={id}` bevat, laad dat specifieke label en open het detail
  // paneel automatisch. `shipment_id` is de canonical alias (Iteratie A
  // — Nav/Search fix); `label` blijft voor BestellingDetail-tijdlijn.
  //
  // Iteratie A · issue #2 · behoud de query-param zodat F5-refresh het
  // detail opnieuw opent (user vereiste: "refresh behoudt detailcontext").
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const targetId = params.get("shipment_id") || params.get("label");
      if (!targetId) return;
      let cancelled = false;
      (async () => {
        try {
          const r = await adminApi.get(`/admin/shipping-labels/${targetId}`);
          if (cancelled) return;
          setSelected(r.data);
          // NB: query-param wordt bewust NIET verwijderd. Bij F5 opent
          // hetzelfde detail opnieuw automatisch. Sluiten van het detail
          // (setSelected(null)) triggert elders de URL cleanup.
        } catch (e) {
          toast.error(formatApiError(e) || "Zending kon niet worden geopend");
        }
      })();
      return () => { cancelled = true; };
    } catch (_) { /* noop */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { limit: 200 };
        if (q.trim()) params.q = q.trim();
        if (statusF) params.status = statusF;
        if (serviceF) params.service_type = serviceF;
        if (payF) params.payment_status = payF;
        if (rangeF) params.range_ = rangeF;
        if (recipientEmailFilter) params.recipient_email = recipientEmailFilter;
        // P0 SPRINT — vertaal lifecycle chip naar backend query-params.
        // Deze filters wijzigen NOOIT de "is verborgen" logica — ze
        // scopen alleen wat in de lijst getoond wordt op dit moment.
        const chip = lifecycleFilters.find((f) => f.key === lifecycleF);
        if (chip && chip.key !== "all") {
          if (chip.type === "flow") params.flow_bucket = chip.key;
          else if (chip.type === "release") params.release_status = "hold";
          else if (chip.type === "status") params.status = "geannuleerd";
          else if (chip.type === "archive") params.archived = "only";
          else if (chip.type === "deleted") params.deleted_only = "true";
        }
        const r = await adminApi.get("/admin/shipping-labels", { params });
        if (!cancelled) {
          setItems(r.data?.items || []);
          setFlowCounts(r.data?.flow_counts || {});
          setTotalCount(r.data?.total || 0);
        }
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err) || "Laden mislukt");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [q, statusF, serviceF, payF, rangeF, reloadTick, recipientEmailFilter, lifecycleF, lifecycleFilters]);

  return (
    <div className="space-y-3" data-testid="labels-overview">
      {/* P0 SPRINT — Lifecycle filter chips.
          Elke chip is een pure UI-filter, geen record wordt ooit uit
          de database verborgen. Chip "Alle actief" is de safe default. */}
      <div className="flex flex-wrap gap-1.5" data-testid="labels-lifecycle-chips">
        {lifecycleFilters.map((f) => {
          const isActive = lifecycleF === f.key;
          // Count derivatie waar mogelijk
          let count = null;
          if (f.type === "flow") count = flowCounts[f.key];
          else if (f.key === "all") count = totalCount;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setLifecycleF(f.key)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                isActive
                  ? "bg-[#0A192F] text-white border-[#0A192F]"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
              data-testid={`labels-chip-${f.key}`}
            >
              <span>{f.label}</span>
              {count !== null && count !== undefined && (
                <span className={`inline-flex items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${
                  isActive ? "bg-white/20 text-white" : "bg-gray-100 text-gray-600"
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); if (!e.target.value) setScanNumber(""); }}
            onKeyDown={e => { if (e.key === "Enter") setScanNumber(q.trim()); }}
            onBlur={() => { if (q.trim()) setScanNumber(q.trim()); }}
            placeholder="Tracking of W-nummer, naam, telefoon…"
            className="pl-8 rounded-xl h-9 text-sm"
            data-testid="labels-search"
          />
        </div>
        <Select value={rangeF || "all"} onValueChange={(v) => setRangeF(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 text-xs rounded-xl w-32" data-testid="labels-filter-range"><SelectValue placeholder="Periode" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle periodes</SelectItem>
            <SelectItem value="today">Vandaag</SelectItem>
            <SelectItem value="this_week">Deze week</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusF || "all"} onValueChange={(v) => setStatusF(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 text-xs rounded-xl w-32" data-testid="labels-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            <SelectItem value="aangemaakt">Aangemaakt</SelectItem>
            <SelectItem value="gedownload">Gedownload</SelectItem>
            <SelectItem value="geannuleerd">Geannuleerd</SelectItem>
          </SelectContent>
        </Select>
        <Select value={serviceF || "all"} onValueChange={(v) => setServiceF(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 text-xs rounded-xl w-40" data-testid="labels-filter-service"><SelectValue placeholder="Service" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle services</SelectItem>
            {(meta?.service_types || []).map((s) => (
              <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={payF || "all"} onValueChange={(v) => setPayF(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 text-xs rounded-xl w-36" data-testid="labels-filter-payment"><SelectValue placeholder="Betaalstatus" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle betaalstatussen</SelectItem>
            {(meta?.payment_statuses || []).map((p) => (
              <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={onCreateNew} className="rounded-xl bg-[#082c50] text-white text-xs" data-testid="labels-new-btn">
          <Plus className="w-4 h-4 mr-1" /> Nieuw label
        </Button>
        {canLookup && (
          <Button onClick={() => setLookupOpen(true)}
                  variant="outline"
                  className="rounded-xl text-xs border-[#082c50] text-[#082c50] hover:bg-[#082c50] hover:text-white"
                  data-testid="labels-lookup-btn">
            <Search className="w-4 h-4 mr-1" /> Zending opzoeken
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-gray-500 border border-dashed border-gray-300 bg-white space-y-2" data-testid="labels-empty">
          <div className="font-bold text-gray-700">Geen zendingen in deze weergave</div>
          <div className="text-xs">
            {lifecycleF === "all" && !q.trim() && !statusF && !serviceF && !payF && !rangeF
              ? "Er zijn nog geen actieve zendingen. Nieuw label toevoegen via de knop hierboven."
              : "Geen resultaten met de huidige filters. Wis filters om alle zendingen te zien."}
          </div>
          {(lifecycleF !== "all" || q.trim() || statusF || serviceF || payF || rangeF) && (
            <Button
              size="sm" variant="outline" className="rounded-xl text-xs mt-2"
              onClick={() => {
                setLifecycleF("all"); setQ(""); setStatusF(""); setServiceF(""); setPayF(""); setRangeF("");
              }}
              data-testid="labels-clear-filters"
            >
              Alle filters wissen
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-600 uppercase font-chivo">
              <tr>
                <th className="p-2 text-left">Trackingnummer</th>
                <th className="p-2 text-left">Ontvanger</th>
                <th className="p-2 text-left">Service</th>
                <th className="p-2 text-left">Bestemming</th>
                <th className="p-2 text-left">Lifecycle</th>
                <th className="p-2 text-left">Aangemaakt</th>
                <th className="p-2 text-center">Status</th>
                <th className="p-2 text-right">Acties</th>
              </tr>
            </thead>
            <tbody>
              {items.map((label) => {
                const currentStatus = label.operational_status || label.status;
                const st = STATUS_BADGE[currentStatus] || { ...STATUS_BADGE.aangemaakt, label: currentStatus };
                const flowLabel = label.warehouse_flow_label;
                const isArchived = !!label.archived_at;
                const isHold = (label.release_status || "").toLowerCase() === "hold";
                return (
                  <tr key={label.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`label-row-${label.id}`}>
                    <td className="p-2 font-mono font-bold text-[#082c50]">{label.tracking_number}</td>
                    <td className="p-2">{(label.recipient || {}).name || "—"}</td>
                    <td className="p-2 uppercase text-[10px] font-bold">{label.service_type}</td>
                    <td className="p-2">{(label.shipment_details || {}).destination || "—"}</td>
                    <td className="p-2">
                      <div className="flex items-center gap-1 flex-wrap">
                        {flowLabel && (
                          <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-semibold">
                            {flowLabel}
                          </span>
                        )}
                        {isHold && (
                          <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-bold">
                            HOLD
                          </span>
                        )}
                        {isArchived && (
                          <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-semibold">
                            Gearchiveerd
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-2 text-gray-500">{fmtDate(label.created_at)}</td>
                    <td className="p-2 text-center">
                      <span data-testid={`label-current-status-${label.id}`} className={`inline-block max-w-full break-words text-[10px] uppercase font-bold px-2 py-0.5 border ${st.cls}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="p-2 text-right">
                      <Button size="sm" variant="outline" className="rounded-xl text-xs" onClick={() => setSelected(label)} data-testid={`label-open-${label.id}`}>
                        Openen
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ShipmentScanResult number={scanNumber} />
      {selected && (
        <LabelDetailDialog
          label={selected}
          meta={meta}
          onClose={() => setSelected(null)}
          onChanged={() => triggerReload()}
        />
      )}
      {lookupOpen && (
        <ShipmentLookupDialog
          onClose={() => setLookupOpen(false)}
          onOpenShipment={(ship) => {
            if (ship?.tracking_number) {
              const url = new URL(window.location.href);
              url.searchParams.set("section", "warehouse");
              url.searchParams.set("w", ship.tracking_number);
              window.location.href = url.toString();
            }
          }}
        />
      )}
    </div>
  );
}

// =================== Detail dialog ===================

function LabelDetailDialog({ label: labelInitial, meta, onClose, onChanged }) {
  const { user } = useAuth();
  const userRole = (user?.role || "").toLowerCase();
  const isMedewerker = userRole === "medewerker";

  const [label, setLabel] = useState(labelInitial);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTick, setPreviewTick] = useState(0);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const isCancelled = label.status === "geannuleerd";
  const currentStatus = label.operational_status || label.status;

  // Preview fetch effect — re-runs whenever the user requests a refresh
  // (via setPreviewTick) or the label id changes. Inline rather than in a
  // useCallback to keep the React 19 'set-state-in-effect' static analyser
  // satisfied.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ts = Date.now();
        const r = await adminApi.get(
          `/admin/shipping-labels/${label.id}/preview.png?_ts=${ts}`,
          { responseType: "blob" },
        );
        if (cancelled) return;
        const blob = new Blob([r.data], { type: "image/png" });
        const newUrl = URL.createObjectURL(blob);
        setPreviewUrl((prev) => {
          if (prev) { try { URL.revokeObjectURL(prev); } catch (e) { void e; } }
          return newUrl;
        });
      } catch (err) { void err; /* silent — keep stale preview */ }
    })();
    return () => { cancelled = true; };
  }, [label.id, previewTick]);

  useEffect(() => () => {
    if (previewUrl) { try { URL.revokeObjectURL(previewUrl); } catch (e) { void e; } }
  }, [previewUrl]);

  const refreshPreview = () => setPreviewTick((n) => n + 1);

  const handleDownload = async () => {
    setBusy(true);
    try {
      const r = await adminApi.get(`/admin/shipping-labels/${label.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${label.tracking_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Label PDF gedownload");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err) || "PDF download mislukt");
    } finally {
      setBusy(false);
    }
  };

  const handleDuplicate = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/shipping-labels/${label.id}/duplicate`);
      toast.success(`Dupliceer aangemaakt: ${r.data.tracking_number}`);
      onChanged();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err) || "Dupliceren mislukt");
    } finally {
      setBusy(false);
    }
  };

  // ----- Sprint 1: W-nummer koppeling -----
  const [externalInput, setExternalInput] = useState(label.linked_external_tracking_number || "");
  const [externalNote, setExternalNote] = useState("");
  const [externalBusy, setExternalBusy] = useState(false);

  const handleLinkExternal = async () => {
    setExternalBusy(true);
    try {
      const payload = {
        external_tracking_number: externalInput.trim() || null,
      };
      if (externalNote.trim()) payload.note = externalNote.trim();
      const r = await adminApi.patch(
        `/admin/shipping-labels/${label.id}/external-link`, payload,
      );
      setLabel(r.data);
      setExternalInput(r.data.linked_external_tracking_number || "");
      setExternalNote("");
      toast.success(
        r.data.linked_external_tracking_number
          ? `Gekoppeld aan ${r.data.linked_external_tracking_number}`
          : "W-nummer ontkoppeld",
      );
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err) || "Koppelen mislukt");
    } finally {
      setExternalBusy(false);
    }
  };

  const handleUnlinkExternal = async () => {
    setExternalBusy(true);
    try {
      const r = await adminApi.patch(
        `/admin/shipping-labels/${label.id}/external-link`,
        { external_tracking_number: null },
      );
      setLabel(r.data);
      setExternalInput("");
      setExternalNote("");
      toast.success("W-nummer ontkoppeld");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err) || "Ontkoppelen mislukt");
    } finally {
      setExternalBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="label-detail-dialog">
        <ShipmentPrealertLink shipmentId={labelInitial.id} />
        <CentralAgentReceipt shipment={label} onReceived={async row => { setLabel(row); refreshPreview(); onChanged(); }} />
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-xl text-[#082c50] flex items-center gap-2 flex-wrap">
            <Tag className="w-5 h-5" /> {label.tracking_number}
            <span data-testid="label-detail-current-status" className={`max-w-full break-words text-[10px] uppercase font-bold px-2 py-0.5 border ${(STATUS_BADGE[currentStatus] || {}).cls}`}>
              {(STATUS_BADGE[currentStatus] || {}).label || currentStatus}
            </span>
            {label.linked_external_tracking_number && (
              <span
                className="text-[10px] uppercase font-bold px-2 py-0.5 bg-[#082c50]/10 text-[#082c50] border border-[#082c50]/20"
                data-testid="label-external-link-badge"
              >
                Gekoppeld aan {label.linked_external_tracking_number}
              </span>
            )}
            {label.use_custom_override && label.custom_status?.code && (
              <span
                className="text-[10px] uppercase font-bold px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 rounded"
                data-testid="label-custom-status-badge"
                title={`Custom status: ${label.custom_status.public_label || label.custom_status.code}`}
              >
                Custom status actief
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {(label.recipient || {}).name} · {label.service_type?.toUpperCase()}
            {label.cancel_reason && <><br /><span className="text-red-700">Reden annulering: {label.cancel_reason}</span></>}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-gray-200">
          <Button size="sm" onClick={handleDownload} disabled={busy} className="bg-[#082c50] text-white rounded-xl text-xs" data-testid="label-download-pdf">
            <Download className="w-3 h-3 mr-1" /> PDF downloaden
          </Button>
          <Button size="sm" variant="outline" onClick={handleDuplicate} disabled={busy} className="rounded-xl text-xs" data-testid="label-duplicate-btn">
            <Copy className="w-3 h-3 mr-1" /> Dupliceren
          </Button>
          {!isCancelled && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditOpen(true)}
              disabled={busy}
              className="rounded-xl text-xs border-[#082c50]/30 text-[#082c50] hover:bg-[#082c50]/5"
              data-testid="label-edit-btn"
            >
              <Pencil className="w-3 h-3 mr-1" /> Label bewerken
            </Button>
          )}
          {!isMedewerker && !isCancelled && (
            <Button size="sm" variant="outline" onClick={() => setCancelOpen(true)} disabled={busy} className="rounded-xl text-xs border-red-200 text-red-700 hover:bg-red-50" data-testid="label-cancel-btn">
              <XCircle className="w-3 h-3 mr-1" /> Annuleren
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={refreshPreview} disabled={busy} className="rounded-xl text-xs" data-testid="label-preview-refresh">
            <RefreshCw className="w-3 h-3 mr-1" /> Vernieuwen
          </Button>
        </div>

        {/* Sprint 1 — W-nummer koppeling */}
        {!isMedewerker && !isCancelled && (
          <div
            className="bg-[#082c50]/5 border border-[#082c50]/15 rounded-md p-3 my-3"
            data-testid="label-external-link-section"
          >
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <div>
                <h4 className="font-chivo font-bold text-xs text-[#082c50] uppercase tracking-wide">
                  Externe tracking (W-nummer)
                </h4>
                <p className="text-[11px] text-gray-600">
                  Koppel dit label aan een extern ManagerCargo W-nummer zodat de publieke tracking-module beide nummers kan vinden.
                </p>
              </div>
              {label.linked_external_tracking_number && (
                <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-emerald-100 text-emerald-800 border border-emerald-300 rounded">
                  Gekoppeld
                </span>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={externalInput}
                onChange={(e) => setExternalInput(e.target.value.toUpperCase())}
                placeholder="W123456789"
                className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-mono focus:border-[#082c50] focus:ring-1 focus:ring-[#082c50] outline-none"
                data-testid="label-external-link-input"
              />
              <input
                type="text"
                value={externalNote}
                onChange={(e) => setExternalNote(e.target.value)}
                placeholder="Notitie (optioneel)"
                className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-[#082c50] focus:ring-1 focus:ring-[#082c50] outline-none"
                data-testid="label-external-link-note"
              />
              <Button
                size="sm"
                onClick={handleLinkExternal}
                disabled={externalBusy}
                className="bg-[#082c50] text-white rounded-md text-xs whitespace-nowrap"
                data-testid="label-external-link-save"
              >
                {label.linked_external_tracking_number ? "Bijwerken" : "Koppelen"}
              </Button>
              {label.linked_external_tracking_number && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleUnlinkExternal}
                  disabled={externalBusy}
                  className="rounded-md text-xs border-red-200 text-red-700 hover:bg-red-50 whitespace-nowrap"
                  data-testid="label-external-link-remove"
                >
                  Ontkoppelen
                </Button>
              )}
            </div>
            {label.linked_external_tracking_number && (
              <div className="text-[10px] text-gray-500 mt-2">
                Bijgewerkt door {label.external_link_updated_by_email || "—"}
                {label.external_link_updated_at ? " · " + new Date(label.external_link_updated_at).toLocaleString("nl-NL") : ""}
                {label.external_link_note ? <><br/>Notitie: {label.external_link_note}</> : null}
              </div>
            )}
          </div>
        )}

        {/* Sprint 2 — Custom Tracking Status CMS */}
        {!isCancelled && (
          <CustomStatusSection
            label={label}
            setLabel={setLabel}
            onChanged={onChanged}
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-[1fr_360px] gap-4">
          <div className="space-y-2 text-sm">
            <DetailRow label="Trackingnummer" value={label.tracking_number} mono />
            <DetailRow label="Service" value={label.service_type?.toUpperCase()} />
            <DetailRow label="Betaalstatus" value={label.payment_status?.toUpperCase()} />
            <DetailRow label="Aangemaakt" value={fmtDate(label.created_at)} />
            <DetailRow label="Aangemaakt door" value={label.created_by_email || "—"} />
            <DetailRow label="PDF gegenereerd" value={label.pdf_generated_at ? fmtDate(label.pdf_generated_at) : "—"} />
            <div className="border-t border-gray-200 pt-2 mt-2">
              <h4 className="font-chivo font-bold text-xs text-gray-600">ONTVANGER</h4>
              <p className="text-sm">{(label.recipient || {}).name}</p>
              {(label.recipient || {}).company && <p className="text-xs">{label.recipient.company}</p>}
              <p className="text-xs text-gray-600">
                {(label.recipient || {}).street} {(label.recipient || {}).house_number}<br />
                {(label.recipient || {}).postal_code} {(label.recipient || {}).city}<br />
                {(label.recipient || {}).country}
              </p>
            </div>
            <div className="border-t border-gray-200 pt-2">
              <h4 className="font-chivo font-bold text-xs text-gray-600">AFZENDER</h4>
              <p className="text-sm">{(label.sender || {}).name || "—"}</p>
              <p className="text-xs text-gray-600">
                {(label.sender || {}).street} {(label.sender || {}).house_number}<br />
                {(label.sender || {}).postal_code} {(label.sender || {}).city}
              </p>
            </div>
            {label.handling_options && label.handling_options.length > 0 && (
              <div className="border-t border-gray-200 pt-2">
                <h4 className="font-chivo font-bold text-xs text-gray-600">HANDLING</h4>
                <p className="text-sm">{label.handling_options.join(" · ").toUpperCase()}</p>
              </div>
            )}
            {label.linked_record?.id && (
              <div className="border-t border-gray-200 pt-2">
                <h4 className="font-chivo font-bold text-xs text-gray-600">GEKOPPELD AAN</h4>
                <p className="text-sm">{label.linked_record.type}: {label.linked_record.label || label.linked_record.id}</p>
              </div>
            )}
          </div>
          <div className="bg-gray-50 border border-gray-200 p-2 flex items-center justify-center min-h-[280px]">
            {previewUrl ? (
              <img src={previewUrl} alt="Label preview" className="max-w-full max-h-[500px] object-contain shadow border border-gray-300" data-testid="label-detail-preview-img" />
            ) : (
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            )}
          </div>
        </div>

        {editOpen && (
          <LabelEditDialog
            label={label}
            meta={meta}
            onClose={() => setEditOpen(false)}
            onSaved={(updated) => {
              setLabel(updated);
              refreshPreview();
              onChanged();
            }}
          />
        )}

        {cancelOpen && (
          <CancelLabelDialog
            label={label}
            onClose={() => setCancelOpen(false)}
            onDone={async () => {
              setCancelOpen(false);
              try {
                const r = await adminApi.get(`/admin/shipping-labels/${label.id}`);
                setLabel(r.data);
                onChanged();
              } catch (err) { void err; }
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex">
      <div className="w-40 text-gray-500 text-xs uppercase font-bold">{label}</div>
      <div className={`flex-1 ${mono ? "font-mono font-bold text-[#082c50]" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function CancelLabelDialog({ label, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const handleConfirm = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/shipping-labels/${label.id}/cancel`, { reason: reason.trim() || null });
      toast.success(`Label ${label.tracking_number} geannuleerd`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err) || "Annuleren mislukt");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="label-cancel-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-red-700 flex items-center gap-2">
            <XCircle className="w-5 h-5" /> Label annuleren?
          </DialogTitle>
          <DialogDescription>
            Label {label.tracking_number} wordt op status &quot;Geannuleerd&quot; gezet. Het trackingnummer blijft gereserveerd.
          </DialogDescription>
        </DialogHeader>
        <Label className="text-[10px] uppercase font-bold">Reden (optioneel)</Label>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={3} className="rounded-xl text-xs" data-testid="label-cancel-reason" />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-xl text-xs">Terug</Button>
          <Button onClick={handleConfirm} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs" data-testid="label-cancel-confirm">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <XCircle className="w-3 h-3 mr-1" />}
            Ja, annuleren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =================== Wizard ===================

function LabelWizard({ meta, onSaved }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    label_type: "package",
    service_type: "package",
    payment_status: "not_paid",
    sender: { ...EMPTY_PARTY },
    recipient: { ...EMPTY_PARTY, country: "Nederland" },
    shipment_details: { ...EMPTY_SHIPMENT },
    handling_options: [],
    linked_record: null,
    origin_code: "NL",
    origin_label: "Rotterdam HQ",
  });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const STEPS = [
    "Type label", "Afzender", "Ontvanger", "Zending",
    "Handling", "Koppeling", "Preview & Download",
  ];

  const updateField = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const canNext = () => {
    if (step === 0) return !!form.service_type && !!form.label_type;
    if (step === 2) return !!form.recipient.name?.trim(); // ontvanger naam verplicht
    return true;
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      // numerieke velden naar Number casten (lege strings weg)
      const sd = { ...form.shipment_details };
      ["piece_count", "weight_kg", "length_cm", "width_cm", "height_cm", "value"].forEach((k) => {
        if (sd[k] === "" || sd[k] == null) delete sd[k];
        else sd[k] = Number(sd[k]);
      });
      const payload = { ...form, shipment_details: sd };
      const r = await adminApi.post("/admin/shipping-labels", payload);
      setCreated(r.data);
      toast.success(`Label aangemaakt: ${r.data.tracking_number}`);
      // Fetch preview blob
      setPreviewLoading(true);
      try {
        const pr = await adminApi.get(`/admin/shipping-labels/${r.data.id}/preview.png?_ts=${Date.now()}`, { responseType: "blob" });
        const blob = new Blob([pr.data], { type: "image/png" });
        setPreviewUrl(URL.createObjectURL(blob));
      } catch (err) { void err; }
      finally { setPreviewLoading(false); }
    } catch (err) {
      toast.error(formatApiError(err) || "Opslaan mislukt");
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    if (!created) return;
    setBusy(true);
    try {
      const r = await adminApi.get(`/admin/shipping-labels/${created.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${created.tracking_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Label PDF gedownload");
    } catch (err) {
      toast.error(formatApiError(err) || "PDF download mislukt");
    } finally { setBusy(false); }
  };

  const reset = () => {
    setStep(0);
    setForm({
      label_type: "package", service_type: "package", payment_status: "not_paid",
      sender: { ...EMPTY_PARTY }, recipient: { ...EMPTY_PARTY, country: "Nederland" },
      shipment_details: { ...EMPTY_SHIPMENT }, handling_options: [],
      linked_record: null, origin_code: "NL", origin_label: "Rotterdam HQ",
    });
    setCreated(null);
    if (previewUrl) { try { URL.revokeObjectURL(previewUrl); } catch (err) { void err; } setPreviewUrl(null); }
  };

  return (
    <div className="bg-white border border-gray-200 p-4 space-y-4" data-testid="label-wizard">
      {/* Stepper */}
      <div className="flex flex-wrap items-center gap-1 text-[10px] uppercase font-chivo">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <span className={`px-2 py-1 ${i === step ? "bg-[#082c50] text-white" : i < step ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
              {i + 1}. {s}
            </span>
            {i < STEPS.length - 1 && <span className="text-gray-300">›</span>}
          </React.Fragment>
        ))}
      </div>

      {/* Step content */}
      {step === 0 && <StepType form={form} updateField={updateField} meta={meta} />}
      {step === 1 && <StepParty title="Afzender" party={form.sender} update={(p, v) => updateField(`sender.${p}`, v)} />}
      {step === 2 && <StepParty title="Ontvanger" party={form.recipient} update={(p, v) => updateField(`recipient.${p}`, v)} required />}
      {step === 3 && <StepShipment form={form} updateField={updateField} />}
      {step === 4 && <StepHandling form={form} updateField={updateField} meta={meta} />}
      {step === 5 && <StepLink form={form} updateField={updateField} meta={meta} />}
      {step === 6 && (
        <StepPreview
          form={form} created={created} busy={busy}
          previewUrl={previewUrl} previewLoading={previewLoading}
          onSave={handleSave} onDownload={handleDownload}
          onReset={reset} onSaved={onSaved}
        />
      )}

      {/* Navigatie */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <Button
          variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0 || busy} className="rounded-xl text-xs"
          data-testid="wizard-prev"
        >
          <ArrowLeft className="w-3 h-3 mr-1" /> Vorige
        </Button>
        <span className="text-xs text-gray-500">Stap {step + 1} / {STEPS.length}</span>
        {step < STEPS.length - 1 ? (
          <Button
            onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
            disabled={!canNext() || busy} className="bg-[#082c50] text-white rounded-xl text-xs"
            data-testid="wizard-next"
          >
            Volgende <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        ) : (
          <span />
        )}
      </div>
    </div>
  );
}

function StepType({ form, updateField, meta }) {
  return (
    <div className="space-y-3" data-testid="wizard-step-type">
      <h3 className="font-chivo font-bold text-base">1. Type label en service</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase">Service-type</Label>
          <Select value={form.service_type} onValueChange={(v) => updateField("service_type", v)}>
            <SelectTrigger className="rounded-xl text-xs h-10" data-testid="wizard-service-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(meta?.service_types || []).map((s) => (
                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase">Betaalstatus</Label>
          <Select value={form.payment_status} onValueChange={(v) => updateField("payment_status", v)}>
            <SelectTrigger className="rounded-xl text-xs h-10" data-testid="wizard-payment-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(meta?.payment_statuses || []).map((p) => (
                <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase">Herkomst</Label>
          <Select value={form.origin_code} onValueChange={(v) => updateField("origin_code", v)}>
            <SelectTrigger className="rounded-xl text-xs h-10" data-testid="wizard-origin-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(meta?.origin_codes || []).map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase">Vestiging-label</Label>
          <Input value={form.origin_label || ""} onChange={(e) => updateField("origin_label", e.target.value)} className="rounded-xl text-xs h-10" data-testid="wizard-origin-label" />
        </div>
      </div>
    </div>
  );
}

function StepParty({ title, party, update, required }) {
  const f = (k, label, type = "text") => (
    <div>
      <Label className="text-[10px] uppercase">{label}{required && k === "name" && <span className="text-red-600"> *</span>}</Label>
      <Input
        value={party[k] || ""}
        onChange={(e) => update(k, e.target.value)}
        type={type}
        className="rounded-xl text-xs h-9"
        data-testid={`wizard-${title.toLowerCase()}-${k}`}
      />
    </div>
  );
  return (
    <div className="space-y-3" data-testid={`wizard-step-${title.toLowerCase()}`}>
      <h3 className="font-chivo font-bold text-base">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {f("name", "Naam")}
        {f("company", "Bedrijf")}
        {f("phone", "Telefoon")}
        {f("email", "E-mail", "email")}
        {f("street", "Straat")}
        {f("house_number", "Huisnummer")}
        {f("postal_code", "Postcode")}
        {f("city", "Stad")}
        {f("country", "Land")}
      </div>
    </div>
  );
}

function StepShipment({ form, updateField }) {
  const sd = form.shipment_details;
  const colliCount = (sd?.items || []).length;
  const f = (k, label, type = "text") => (
    <div>
      <Label className="text-[10px] uppercase">{label}</Label>
      <Input
        value={sd[k] != null ? sd[k] : ""}
        onChange={(e) => updateField(`shipment_details.${k}`, e.target.value)}
        type={type}
        className="rounded-xl text-xs h-9"
        data-testid={`wizard-shipment-${k}`}
      />
    </div>
  );
  return (
    <div className="space-y-3" data-testid="wizard-step-shipment">
      <h3 className="font-chivo font-bold text-base">4. Zendinggegevens</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {f("piece_count", "Aantal colli", "number")}
        {f("weight_kg", "Gewicht (kg)", "number")}
        {f("destination", "Bestemming")}
        {f("length_cm", "Lengte (cm)", "number")}
        {f("width_cm", "Breedte (cm)", "number")}
        {f("height_cm", "Hoogte (cm)", "number")}
        {f("value", "Waarde (optioneel)", "number")}
        {f("currency", "Valuta")}
      </div>
      <div>
        <Label className="text-[10px] uppercase">Inhoudsomschrijving</Label>
        {colliCount > 0 ? (
          <div className="border border-gray-200 bg-gray-50 rounded-xl p-2"
               data-testid="wizard-shipment-contents-derived">
            <p className="text-xs text-gray-700 break-words">
              {sd.contents_description || "—"}
            </p>
            <p className="text-[10px] text-gray-500 mt-1">
              Inhoud wordt automatisch opgebouwd uit de colli van deze zending.
            </p>
          </div>
        ) : (
          <Textarea
            value={sd.contents_description || ""}
            onChange={(e) => updateField("shipment_details.contents_description", e.target.value)}
            rows={2}
            className="rounded-xl text-xs"
            data-testid="wizard-shipment-contents"
          />
        )}
      </div>
      <div>
        <Label className="text-[10px] uppercase">Interne opmerking</Label>
        <Textarea
          value={sd.internal_note || ""}
          onChange={(e) => updateField("shipment_details.internal_note", e.target.value)}
          rows={2}
          className="rounded-xl text-xs"
          data-testid="wizard-shipment-note"
        />
      </div>
    </div>
  );
}

function StepHandling({ form, updateField, meta }) {
  const toggle = (key) => {
    const set = new Set(form.handling_options);
    if (set.has(key)) set.delete(key); else set.add(key);
    updateField("handling_options", Array.from(set));
  };
  const opts = (meta?.handling_options || []).filter((o) => o.key !== "flammable");
  return (
    <div className="space-y-3" data-testid="wizard-step-handling">
      <h3 className="font-chivo font-bold text-base">5. Handling opties</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {opts.map((o) => (
          <label key={o.key} className="flex items-center gap-2 p-2 border border-gray-200 cursor-pointer hover:bg-gray-50" data-testid={`wizard-handling-${o.key}`}>
            <Checkbox
              checked={form.handling_options.includes(o.key)}
              onCheckedChange={() => toggle(o.key)}
            />
            <span className="text-xs font-chivo font-bold">{o.label}</span>
          </label>
        ))}
      </div>
      <details className="border border-amber-300 p-2 bg-amber-50">
        <summary className="text-xs font-chivo font-bold text-amber-800 cursor-pointer">⚠ Gevaarlijk: Flammable / Brandbaar</summary>
        <div className="mt-2">
          <label className="flex items-center gap-2 text-xs" data-testid="wizard-handling-flammable">
            <Checkbox
              checked={form.handling_options.includes("flammable")}
              onCheckedChange={() => toggle("flammable")}
            />
            FLAMMABLE — alleen markeren als zending bewust brandbare goederen bevat (douane-/operationeel gevoelig).
          </label>
        </div>
      </details>
    </div>
  );
}

function StepLink({ form, updateField, meta }) {
  const types = meta?.linked_record_types || [];
  const lr = form.linked_record || { type: "", id: "", label: "" };
  return (
    <div className="space-y-3" data-testid="wizard-step-link">
      <h3 className="font-chivo font-bold text-base">6. Koppeling (optioneel)</h3>
      <p className="text-xs text-gray-600">Koppel dit label aan een bestaand record voor terugzoeken. Laat leeg als geen koppeling nodig is.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[10px] uppercase">Type</Label>
          <Select value={lr.type || "none"} onValueChange={(v) => {
            if (v === "none") updateField("linked_record", null);
            else updateField("linked_record", { ...lr, type: v });
          }}>
            <SelectTrigger className="rounded-xl text-xs h-10" data-testid="wizard-link-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Geen koppeling</SelectItem>
              {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {lr.type && (
          <>
            <div>
              <Label className="text-[10px] uppercase">Record-ID/nummer</Label>
              <Input value={lr.id || ""} onChange={(e) => updateField("linked_record", { ...lr, id: e.target.value })} className="rounded-xl text-xs h-10" data-testid="wizard-link-id" />
            </div>
            <div>
              <Label className="text-[10px] uppercase">Korte label (voor UI)</Label>
              <Input value={lr.label || ""} onChange={(e) => updateField("linked_record", { ...lr, label: e.target.value })} placeholder="bv. Klant Jan Jansen / Factuur INV-2026-0001" className="rounded-xl text-xs h-10" data-testid="wizard-link-label" />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StepPreview({ form, created, busy, previewUrl, previewLoading, onSave, onDownload, onReset, onSaved }) {
  return (
    <div className="space-y-3" data-testid="wizard-step-preview">
      <h3 className="font-chivo font-bold text-base">7. Preview &amp; Download</h3>
      {!created ? (
        <div className="bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          Klik op &quot;Label opslaan&quot; om een trackingnummer toe te wijzen en het label-PDF te genereren.
          <div className="mt-3 flex gap-2">
            <Button onClick={onSave} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs" data-testid="wizard-save-btn">
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />} Label opslaan
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="bg-emerald-50 border border-emerald-200 p-3 text-sm">
            <div className="flex items-center gap-2 text-emerald-800 font-chivo font-bold">
              <CheckCircle2 className="w-4 h-4" />
              Label opgeslagen — Trackingnummer: <span className="font-mono">{created.tracking_number}</span>
            </div>
          </div>
          {/* Multi-copy melding — toont alleen wanneer piece_count > 1 */}
          {Number((created.shipment_details || {}).piece_count) > 1 && (
            <div
              className="bg-amber-50 border border-amber-300 p-3 text-sm rounded-md"
              data-testid="wizard-multi-copy-notice"
            >
              <div className="font-chivo font-bold text-amber-900">
                Bij download worden {Number(created.shipment_details.piece_count)} labels gegenereerd.
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                Elke pagina toont Colli 1/{Number(created.shipment_details.piece_count)}, 2/{Number(created.shipment_details.piece_count)}, ... — hoofdtrackingnummer blijft hetzelfde.
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_360px] gap-3">
            <div className="space-y-2 text-xs">
              <p className="text-gray-600">Service: <span className="font-bold text-[#082c50]">{created.service_type?.toUpperCase()}</span></p>
              <p className="text-gray-600">Ontvanger: <span className="font-bold">{(created.recipient || {}).name}</span></p>
              <p className="text-gray-600">Bestemming: <span className="font-bold">{(created.shipment_details || {}).destination || "—"}</span></p>
              <p className="text-gray-600">Aantal colli: <span className="font-bold">{(created.shipment_details || {}).piece_count || 1}</span></p>
              <p className="text-gray-600">Status: <span className="font-bold">{created.status?.toUpperCase()}</span></p>
              <div className="flex flex-wrap gap-2 pt-3">
                <Button onClick={onDownload} disabled={busy} className="bg-[#082c50] text-white rounded-xl text-xs" data-testid="wizard-download-btn">
                  <Download className="w-3 h-3 mr-1" />
                  PDF downloaden{Number((created.shipment_details || {}).piece_count) > 1
                    ? ` (${Number(created.shipment_details.piece_count)} labels)`
                    : ""}
                </Button>
                <Button onClick={onReset} variant="outline" className="rounded-xl text-xs" data-testid="wizard-new-btn">
                  <Plus className="w-3 h-3 mr-1" /> Nieuw label maken
                </Button>
                <Button onClick={onSaved} variant="outline" className="rounded-xl text-xs" data-testid="wizard-finish-btn">
                  <FileIcon className="w-3 h-3 mr-1" /> Naar overzicht
                </Button>
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 p-2 flex flex-col items-center justify-center min-h-[260px] gap-2">
              {previewLoading ? (
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              ) : previewUrl ? (
                <>
                  <img src={previewUrl} alt="Label preview" className="max-w-full max-h-[500px] object-contain shadow border border-gray-300" data-testid="wizard-preview-img" />
                  {Number((created.shipment_details || {}).piece_count) > 1 && (
                    <div className="text-[10px] uppercase tracking-wide text-gray-500" data-testid="wizard-preview-label">
                      Voorbeeldlabel 1 van {Number(created.shipment_details.piece_count)}
                    </div>
                  )}
                </>
              ) : (
                <span className="text-xs text-gray-500">Preview laden...</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
